import type {
  SidebarNavItem,
  SidebarNavSubItem,
  SidebarTranslateFn
} from "./types/sidebarNavTypes";
import type {
  SidebarNavConfigChildEntry,
  SidebarNavConfigEntry
} from "./types/sidebarNavConfigTypes";
import { SIDEBAR_ICONS } from "./config/sidebarIcons";
import { RETAIL_SIDEBAR_CONFIG } from "./config/retailSidebarConfig";
import type { UserRole } from "../components/Profile/enums/profileEnums";

const mapChildEntry = (
  entry: SidebarNavConfigChildEntry,
  t: SidebarTranslateFn,
  role: UserRole
): SidebarNavSubItem | null => {
  if (entry.visible && !entry.visible(role)) {
    return null;
  }

  return {
    label: t(entry.labelKey, entry.defaultLabel),
    icon: SIDEBAR_ICONS[entry.icon],
    path: entry.path,
    testId: entry.testId
  };
};

export const buildSidebarNavItems = (
  config: SidebarNavConfigEntry[],
  t: SidebarTranslateFn,
  role: UserRole
): SidebarNavItem[] =>
  config.flatMap((entry) => {
    if (entry.visible && !entry.visible(role)) {
      return [];
    }

    const item: SidebarNavItem = {
      label: t(entry.labelKey, entry.defaultLabel),
      icon: SIDEBAR_ICONS[entry.icon],
      path: entry.path,
      testId: entry.testId
    };

    if (entry.children?.length) {
      item.items = entry.children
        .map((child) => mapChildEntry(child, t, role))
        .filter((child): child is SidebarNavSubItem => child != null);
    }

    if (item.items && item.items.length === 0) {
      return [];
    }

    return [item];
  });

/** Retail sidebar items — used by E2E parity tests and deprecated SidebarConfig. */
export const getRetailSidebar = (
  t: SidebarTranslateFn,
  role: UserRole
): SidebarNavItem[] => buildSidebarNavItems(RETAIL_SIDEBAR_CONFIG, t, role);
